import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import {
  MDSButton,
  MDSButtonVariant,
  MDSCollectionIcon,
  MDSChipInputItem,
  MDSChipInput,
} from "@/mds";
import { AppStore, useAppStore } from "@/store";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { EmailIntegrationOrganizingManualCollections } from "@/store/email-senders/types";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

interface IntegrationsFormattingCollectionsProps {
  organizingManualCollections: string[] | null;
  setOrganizingManualCollections: (value: EmailIntegrationOrganizingManualCollections) => void;
}

export const IntegrationsFormattingCollections = observer<IntegrationsFormattingCollectionsProps>(
  ({ organizingManualCollections, setOrganizingManualCollections }) => {
    const { store } = useAppStore();
    const [chips, setChips] = useState<MDSChipInputItem[]>([]);

    useEffect(() => {
      const loadChips = async () => {
        const chips = await mapCollectionsToChips(organizingManualCollections, store);
        setChips(chips);
      };

      loadChips();
    }, [organizingManualCollections, store]);

    // local state equals one on the store
    const isDisabled =
      organizingManualCollections?.length === chips.length &&
      organizingManualCollections.every(collection => chips.some(chip => chip.id === collection));

    const getChips = async (query: string) => {
      const collections = await store.collections.getMatchingCollections(query);

      return sortCollectionsForChips(collections).map(collection =>
        mapCollectionToChip(collection)
      );
    };

    const updateChips = (
      data: MDSChipInputItem[] | ((prevChips: MDSChipInputItem[]) => MDSChipInputItem[])
    ) => {
      const newChips = typeof data === "function" ? data(chips) : data;

      setChips(newChips);
    };

    return (
      <div>
        <MDSChipInput
          chips={chips}
          setChips={updateChips}
          getChips={getChips}
          onSubmit={() => {}}
          placeholder="Type collection name"
        />

        <LeftStyles>
          <MDSButton
            onClick={() => {
              // revert to local value
              const loadChips = async () => {
                const chips = await mapCollectionsToChips(organizingManualCollections, store);
                setChips(chips);
              };

              loadChips();
            }}
          >
            Cancel
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={() => setOrganizingManualCollections(chips.map(chip => chip.id))}
            isDisabled={isDisabled}
          >
            Save Settings
          </MDSButton>
        </LeftStyles>
      </div>
    );
  }
);

const mapCollectionsToChips = async (
  collectionIds: EmailIntegrationOrganizingManualCollections,
  store: AppStore
): Promise<MDSChipInputItem[]> => {
  if (!collectionIds) return [];

  const collections = await store.collections.getManyAsync(collectionIds);

  return collections.map(collection => mapCollectionToChip(collection));
};

const mapCollectionToChip = (collection: CollectionObservable): MDSChipInputItem => ({
  id: collection.id,
  orderByString: collection.lastInteractedAtByMe ?? collection.lastInteractedAt,
  label: collection.label,
  secondaryLabel: collection.description,
  icon: ({ size, boxSize }) => (
    <MDSCollectionIcon collectionId={collection.id} size={size} boxSize={boxSize} />
  ),
  searchLabels: [collection.label],
});

const LeftStyles = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["1"]};
  margin-top: ${({ theme }) => theme.spacing["2"]};
`;
